import { CopyIcon } from '@phosphor-icons/react'
import { useEffect, useState, useSyncExternalStore, type CSSProperties, type ReactNode } from 'react'
import { initials } from '../logic/format.ts'
import { shortId } from '../logic/ids.ts'
import { formatRelative, formatTimestamp, type Zone } from '../logic/time.ts'
import { cx } from './cx.ts'
import { toast } from './Toast.tsx'
import { Tooltip } from './Tooltip.tsx'

async function copy(value: string, what = 'Copied') {
  try {
    await navigator.clipboard.writeText(value)
    toast({ title: what, description: <span className="rt-mono">{value}</span>, duration: 2500 })
  } catch {
    toast({ title: 'Couldn’t copy', description: 'Your browser blocked clipboard access.', tone: 'warn' })
  }
}

/** A prefixed id in mono: prefix dimmed, body kept, middle elided when tight. Click copies. */
export function Id({ value, full, className, copyable = true }: { value: string | null | undefined; full?: boolean; className?: string; copyable?: boolean }) {
  if (!value) return <span className={cx('rt-id', className)}>—</span>
  const shown = full ? value : shortId(value)
  const i = shown.indexOf('_')
  const body = (
    <>
      {i > 0 ? (
        <>
          <span className="rt-id__prefix">{shown.slice(0, i + 1)}</span>
          <span>{shown.slice(i + 1)}</span>
        </>
      ) : (
        <span>{shown}</span>
      )}
    </>
  )
  if (!copyable) return <span className={cx('rt-id', className)} title={value} style={{ cursor: 'inherit' }}>{body}</span>
  return (
    <Tooltip content={<span className="rt-mono">{value}</span>} side="top">
      <button
        type="button"
        className={cx('rt-id', className)}
        onClick={(e) => {
          e.stopPropagation()
          void copy(value, 'Id copied')
        }}
        aria-label={`Copy ${value}`}
      >
        {body}
        <CopyIcon aria-hidden />
      </button>
    </Tooltip>
  )
}

export function CopyText({ value, className }: { value: string; className?: string }) {
  return (
    <button type="button" className={cx('rt-id', className)} onClick={() => void copy(value)} aria-label={`Copy ${value}`}>
      <span>{value}</span>
      <CopyIcon aria-hidden />
    </button>
  )
}

// One shared clock so a page of relative times re-renders together, twice a minute.
let now = Date.now()
const subs = new Set<() => void>()
let timer: number | undefined
function subscribeNow(cb: () => void) {
  subs.add(cb)
  if (timer === undefined && typeof window !== 'undefined') {
    timer = window.setInterval(() => {
      now = Date.now()
      subs.forEach((s) => s())
    }, 30_000)
  }
  return () => {
    subs.delete(cb)
    if (subs.size === 0 && timer !== undefined) {
      window.clearInterval(timer)
      timer = undefined
    }
  }
}

export function useNow(): number {
  return useSyncExternalStore(subscribeNow, () => now, () => now)
}

export interface TimeProps {
  value: string | number | Date | null | undefined
  /** relative: "5m ago"; absolute: "2026-10-28 14:05:12"; both: absolute + relative. */
  format?: 'relative' | 'absolute' | 'date' | 'both'
  zone?: Zone
  className?: string
  seconds?: boolean
  /** Append "UTC" to absolute UTC times (default). Columns whose header names the zone can drop it. */
  showZone?: boolean
}

export function Time({ value, format = 'relative', zone = 'local', className, seconds = true, showZone = true }: TimeProps) {
  const current = useNow()
  if (value === null || value === undefined || value === '') return <span className={cx('rt-time', className)}>—</span>
  const ms = value instanceof Date ? value.getTime() : typeof value === 'number' ? value : new Date(value).getTime()
  const iso = Number.isFinite(ms) ? new Date(ms).toISOString() : undefined
  const stamp = formatTimestamp(ms, zone, seconds)
  const abs = showZone ? stamp : stamp.replace(/ UTC$/, '')
  const text =
    format === 'relative'
      ? formatRelative(ms, Math.max(current, Date.now()))
      : format === 'absolute'
        ? abs
        : format === 'date'
          ? abs.slice(0, 10)
          : `${abs} · ${formatRelative(ms, current)}`
  return (
    <time className={cx('rt-time', className)} dateTime={iso} title={format === 'relative' ? `${formatTimestamp(ms, 'local')} (${formatTimestamp(ms, 'utc')})` : undefined}>
      {text}
    </time>
  )
}

export interface Property {
  label: ReactNode
  value: ReactNode
  id?: string
}

/** A spec sheet: label/value pairs separated by hairlines. */
export function PropertyList({ items, className, style }: { items: readonly (Property | null | false | undefined)[]; className?: string; style?: CSSProperties }) {
  const list = items.filter(Boolean) as Property[]
  return (
    <dl className={cx('rt-props', className)} style={style}>
      {list.map((p, i) => (
        <PropertyRow key={p.id ?? (typeof p.label === 'string' ? p.label : String(i))} label={p.label} value={p.value} />
      ))}
    </dl>
  )
}

function PropertyRow({ label, value }: Property) {
  return (
    <>
      <dt className="rt-props__k">{label}</dt>
      <dd className="rt-props__v">{value === null || value === undefined || value === '' ? <span className="rt-faint">—</span> : value}</dd>
    </>
  )
}

export interface ReadoutProps {
  label: ReactNode
  value: ReactNode
  unit?: ReactNode
  sub?: ReactNode
  tone?: 'neutral' | 'warn' | 'fault' | 'muted'
  size?: 'sm' | 'md' | 'lg'
  className?: string
}

/** An instrument display: terse label, tabular value, explicit unit. */
export function Readout({ label, value, unit, sub, tone = 'neutral', size = 'md', className }: ReadoutProps) {
  return (
    <div className={cx('rt-readout', className)} data-tone={tone} data-size={size}>
      <span className="rt-label">{label}</span>
      <span className="rt-readout__value">
        {value}
        {unit ? <span className="rt-readout__unit">{unit}</span> : null}
      </span>
      {sub ? <span className="rt-readout__sub">{sub}</span> : null}
    </div>
  )
}

export function Avatar({ name, src, size = 24, className }: { name?: string | null; src?: string | null; size?: number; className?: string }) {
  const [failed, setFailed] = useState(false)
  useEffect(() => setFailed(false), [src])
  return (
    <span className={cx('rt-avatar', className)} style={{ '--av': `${size}px` } as CSSProperties} aria-hidden>
      {src && !failed ? <img src={src} alt="" onError={() => setFailed(true)} /> : initials(name)}
    </span>
  )
}

/**
 * Tiny bar chart (activity density, latency history). Bars are grey; one optional index is
 * emphasised. With `slots`, bars keep a fixed width and fill from the right (newest last).
 */
export function Bars({ values, width = 120, height = 24, active, className, label, slots }: { values: readonly number[]; width?: number; height?: number; active?: number; className?: string; label?: string; slots?: number }) {
  const max = Math.max(1, ...values)
  const n = Math.max(1, slots ?? values.length)
  const offset = slots ? Math.max(0, slots - values.length) : 0
  const gap = n > 60 ? 0 : 1
  const bw = (width - gap * (n - 1)) / n
  return (
    <svg className={cx('rt-spark', className)} width={width} height={height} viewBox={`0 0 ${width} ${height}`} role={label ? 'img' : undefined} aria-label={label} aria-hidden={label ? undefined : true}>
      <line x1={0} x2={width} y1={height - 0.5} y2={height - 0.5} stroke="var(--rt-line)" />
      {values.slice(-n).map((v, i) => {
        const h = v === 0 ? 0 : Math.max(1, (v / max) * (height - 1))
        return <rect key={i} x={(i + offset) * (bw + gap)} y={height - 1 - h} width={Math.max(0.5, bw)} height={h} fill={i === active ? 'var(--rt-signal)' : 'currentColor'} />
      })}
    </svg>
  )
}
